import { HttpClient } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Actions, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { Logger } from '@se/se.ui.html5';
import { PacsStudyData as PacsStudyDataNg1 } from 'estensa-module';
import { BehaviorSubject, firstValueFrom } from 'rxjs';

import { ExtensaService } from '../../connectivity/extensa.service';
import { IsIntegrationWebService } from '../../connectivity/is-integration-web.service';
import {
    BaseSRMessage,
    DEFAULT_OPEN_IMAGES_OPTIONS,
    ExaminationStatusForImaging,
    fromNg1PacsStudyData,
    IViewerHelper,
    keyImageFromMessage,
    KeyImageMessage,
    ModelShowImage,
    ModelUSMeasure,
    ModelUSStressEchoAssociateImages,
    PacsStudyDataVnext,
    PacsTuple,
    RequestModelViewStudies,
    USMeasureMessage,
    USStressEchoInitMessage,
    USStressEchoSelectMessage,
    XmlRequestType,
} from './common';
import { RootState } from '../../root-store/root.state';
import { convertPropertiesToCamelCase } from '../../models/interop';
import {
    seViewerKeyImageAdd,
    seViewerKeyImagesDelete,
    seViewerKeyImagesSet,
    seViewerKeyImageRefresh,
    seViewerSRAdd,
    seViewerSRDelete,
    seViewerUSMeasureDeleteRequest,
    seViewerUSMeasureDeleteResponse,
    seViewerUSMeasureGetReportRequest,
    seViewerUSMeasureGetReportResponse,
    seViewerUSMeasureImportRequest,
    seViewerUSMeasureImportRequestThirdParts,
    seViewerUSStressEchoInit,
    seViewerUSStressEchoSelect,
    seViewerUSKineticMeasureImportRequest,
} from '../../root-store/root.actions';
import { DeepPascalCase } from '../../utils/types';
import { EstensaReviewConfirmationModalComponent } from './estensa-review-confirmation-modal.component';
import { AuthenticationService } from '../authentication/authentication.service';
import { selectExtensaConnected } from '../../connectivity/connectivity.selectors';

@Injectable({
    providedIn: 'root',
})
export class EstensaReviewViewerHelper implements IViewerHelper {
    readonly isVNext = true;
    readonly viewerType = 'EstensaReviewVNext';

    private isExtensaConnected$ = new BehaviorSubject<boolean>(false);
    private isOpeningViewer = false;

    constructor(
        private actions$: Actions,
        private authentication: AuthenticationService,
        private dialog: MatDialog,
        private http: HttpClient,
        private extensaService: ExtensaService,
        private isIntegration: IsIntegrationWebService,
        private logger: Logger,
        private store: Store<RootState>,
        private zone: NgZone,
    ) {}

    async init() {
        if (this.authentication.isOpenedFromWpf) {
            return;
        }

        const hub = this.extensaService.signalrHubConnection.createHubProxy('ReviewHub');

        this.listenForViewerEvents(hub);

        this.listenForActions(hub);

        this.store.pipe(select(selectExtensaConnected)).subscribe(this.isExtensaConnected$);
    }

    /**
     * Apertura Review:
     * - Non fare nulla se Estensa è già aperta e l'utente è già loggato, o se il client è stato aperto da WPF.
     * - Nel caso estensa sia aperta, ma non l'utente è già loggato, fai solo login.
     * - Altrimenti, apri estensa, aspetta che sia disponibie per il login, e poi effettua il login.
     */
    async openViewer(forceIsIntegrationApp?: boolean) {
        if (this.isExtensaConnected$.value || this.isOpeningViewer || this.authentication.isOpenedFromWpf) {
            return;
        }

        this.isOpeningViewer = true;
        try {
            if (await this.extensaService.isAvailable()) {
                await this.extensaService.login();
                return;
            }
        } catch (err) {
            // Ignoriamo l'errore, potrebbe essere che la postazione non è abilitata all'imaging.
            this.logger.debug(err);
        } finally {
            this.isOpeningViewer = false;
        }

        this.isOpeningViewer = true;
        try {
            await this.startEstensa(forceIsIntegrationApp);
        } catch (err) {
            // Ignoriamo l'errore, potrebbe essere che la postazione non è abilitata all'imaging.
            this.logger.debug(err);
        } finally {
            this.isOpeningViewer = false;
        }
    }

    async closeViewer() {
        if (this.authentication.hasSecondaryAuthentication) {
            return;
        }

        return firstValueFrom(this.http.post<any>(`${this.extensaService.getBaseUrl()}/Host/Close`, { force: true }));
    }

    /**
     * Flusso di apertura:
     *   - Se richiesta l'apertura tramite isIntegrationApp (es. alla login), non usare l'apertura da url.
     *     In questo caso l'apertura può fallire senza problemi.
     *   - Altrimenti utilizza l'apertura tramite URL.
     *     Se fallisce (es. popup bloccati), prova con isIntegrationApp.
     */
    async startEstensa(forceIsIntegrationApp: boolean) {
        if (forceIsIntegrationApp) {
            await this.openEstensaByIsIntegration();
        } else if (!this.openEstensaByUrl()) {
            await this.openEstensaByIsIntegration();
        }

        if (await this.extensaService.waitUntilAvailable()) {
            return await this.extensaService.login();
        }
        // waitUntilAvailable è fallita - probabilmente è andata in timeout. Se non abbiamo già provato, apri
        //   estensa con isIntegrationApp.
        if (!forceIsIntegrationApp) {
            const retry = await firstValueFrom(this.dialog.open(EstensaReviewConfirmationModalComponent).afterClosed());
            if (retry) {
                this.isOpeningViewer = false;
                return this.openViewer(true);
            }
        }
    }

    openEstensaByUrl() {
        return window.open('estensa://open');
    }

    async openEstensaByIsIntegration() {
        return this.isIntegration.startEstensa();
    }

    async discardImages(ask = false) {
        if (!this.isReady()) {
            return;
        }

        return firstValueFrom(
            this.http.post<void>(
                `${this.extensaService.getBaseUrl()}/Review/Discard`,
                { ask },
                await this.extensaService.getAuthOptions(),
            ),
        );
    }

    async openImages(studyData: PacsStudyDataVnext[]) {
        if (!this.isReady()) {
            return;
        }

        const requestBody: RequestModelViewStudies = {
            ...DEFAULT_OPEN_IMAGES_OPTIONS,
            studies: studyData.map((datum) => ({
                Tags: {
                    PatientID: datum.patient.patientId,
                    PatientName: datum.patient.fullName,
                    PatientSex: datum.patient.sex,
                    PatientBirthDate: datum.patient.birthDate,
                    StudyDate: datum.study.studyDate,
                    AccessionNumber: datum.study.accessionNumber,
                    StudyInstanceUID: datum.study.studyInstanceUID,
                    Modality: datum.study.modality,
                },
            })),
        };
        return firstValueFrom(
            this.http.post<void>(
                `${this.extensaService.getBaseUrl()}/Review/ViewImages`,
                requestBody,
                await this.extensaService.getAuthOptions(),
            ),
        );
    }

    async setExaminationStatus(idExaminations: string[], examinationStatus: ExaminationStatusForImaging) {
        if (!this.isReady()) {
            return;
        }

        return firstValueFrom(
            this.http.post<void>(
                `${this.extensaService.getBaseUrl()}/Review/ExaminationStatus`,
                { idExaminations, examinationStatus },
                await this.extensaService.getAuthOptions(),
            ),
        );
    }

    async deleteKeyImage?(data: KeyImageMessage): Promise<void> {
        if (!this.isReady()) {
            return;
        }

        return firstValueFrom(
            this.http.post<void>(
                `${this.extensaService.getBaseUrl()}/Review/DeleteKeyImage`,
                data,
                await this.extensaService.getAuthOptions(),
            ),
        );
    }

    async usStressEchoSelectStage?(stage: string): Promise<void> {
        if (!this.isReady()) {
            return;
        }

        return firstValueFrom(
            this.http.post<void>(
                `${this.extensaService.getBaseUrl()}/Review/USStressEchoSelectStage`,
                `"${stage}"`,
                await this.extensaService.getAuthOptions(),
            ),
        );
    }

    async usStressEchoSelectView?(view: string): Promise<void> {
        if (!this.isReady()) {
            return;
        }

        return firstValueFrom(
            this.http.post<void>(
                `${this.extensaService.getBaseUrl()}/Review/USStressEchoSelectView`,
                `"${view}"`,
                await this.extensaService.getAuthOptions(),
            ),
        );
    }

    async uSStressEchoAssociateImages?(model: ModelUSStressEchoAssociateImages): Promise<void> {
        if (!this.isReady()) {
            return;
        }

        return firstValueFrom(
            this.http.post<void>(
                `${this.extensaService.getBaseUrl()}/Review/USStressEchoAssociateImages`,
                model,
                await this.extensaService.getAuthOptions(),
            ),
        );
    }

    async showImage(model: ModelShowImage): Promise<void> {
        if (!this.isReady()) {
            return;
        }

        return firstValueFrom(
            this.http.post<void>(
                `${this.extensaService.getBaseUrl()}/Review/ShowImage`,
                model,
                await this.extensaService.getAuthOptions(),
            ),
        );
    }

    async uSMeasureShow(model: ModelUSMeasure): Promise<void> {
        if (!this.isReady()) {
            return;
        }

        return firstValueFrom(
            this.http.post<void>(
                `${this.extensaService.getBaseUrl()}/Review/USMeasureShow`,
                model,
                await this.extensaService.getAuthOptions(),
            ),
        );
    }

    openImagesRequired(imagingData: PacsStudyDataNg1[]) {
        this.openImages(imagingData.map(fromNg1PacsStudyData));
    }

    discardImagesRequired() {
        this.discardImages();
    }

    closeViewerRequired() {
        this.closeViewer();
    }

    isFormSubmitting() {
        return false;
    }

    private isReady() {
        return this.isExtensaConnected$.value && !this.authentication.isOpenedFromWpf;
    }

    private listenForViewerEvents(hub: SignalR.Hub.Proxy) {
        hub.on('studyInstanceUID', (msg) => this.zone.run(this.onUnimplemented('studyInstanceUID'), this, [msg]))
            // Immagini chiave
            .on('addKeyImage', (msg) => this.zone.run(this.onAddKeyImage, this, [msg]))
            .on('deleteKeyImage', (msg) => this.zone.run(this.onDeleteKeyImage, this, [msg]))
            .on('refreshKeyImage', (msg) => this.zone.run(this.onRefreshKeyImage, this, [msg]))
            .on('keyImagesInfo', (msg) => this.zone.run(this.onSetKeyImages, this, [msg]))
            // Misure US
            .on('usMeasureDeleteSourceAndImportToReport', (measure: any, callback: string, requestId: string) =>
                this.zone.run(this.onUsMeasureDelete, this, [measure, callback, requestId]),
            )
            .on('usMeasureGetReportInfo', (npla: any, callback: string, requestId: string) =>
                this.zone.run(this.onUsMeasureGetReportInfo, this, [npla, callback, requestId]),
            )
            .on('usMeasureImportToReport', (msg) => this.zone.run(this.onUsMeasureImport, this, [msg]))
            // Oggetti Structured Report
            .on('addStructuredReport', (msg) => this.zone.run(this.onSrAdd, this, [msg]))
            .on('deleteStructuredReport', (msg) => this.zone.run(this.onSrDelete, this, [msg]))
            // Oggetti Key Object Selection
            .on('addKeyObjectSelection', (msg) =>
                this.zone.run(this.onUnimplemented('addKeyObjectSelection'), this, [msg]),
            )
            .on('deleteKeyObjectSelection', (msg) =>
                this.zone.run(this.onUnimplemented('addKeyObjectSelection'), this, [msg]),
            )
            // Risultati XML da applicativi esterni
            .on('xmlCAASData', (msg) => this.zone.run(this.onXmlImport(XmlRequestType.CaasQardia), this, [msg]))
            .on('xmlTeraReconData', (msg) => this.zone.run(this.onXmlImport(XmlRequestType.TeraRecon), this, [msg]))
            // Stress Echo
            .on('usStressEchoInit', (msg) => this.zone.run(this.onUsStressEchoInit, this, [msg]))
            .on('usStressEchoSelect', (msg) => this.zone.run(this.onUsStressEchoSelect, this, [msg]))
            // Left Ventricle Strain
            .on('lvStrainStart', (msg) => this.zone.run(this.onUnimplemented('lvStrainStart'), this, [msg]))
            .on('lvStrainEnd', (msg) => this.zone.run(this.onUnimplemented('lvStrainEnd'), this, [msg]))
            .on('lvStrainResultClear', (msg) => this.zone.run(this.onUnimplemented('lvStrainResult'), this, [msg]))
            .on('lvStrainResult', (msg) =>
                this.zone.run(this.onXmlImport(XmlRequestType.DiaImagingLVStrain), this, [msg]),
            )
            // Right Ventricle Strain
            .on('rvStrainStart', (msg) => this.zone.run(this.onUnimplemented('rvStrainStart'), this, [msg]))
            .on('rvStrainEnd', (msg) => this.zone.run(this.onUnimplemented('rvStrainEnd'), this, [msg]))
            .on('rvStrainResultClear', (msg) => this.zone.run(this.onUnimplemented('rvStrainResultClear'), this, [msg]))
            .on('rvStrainResult', (msg) =>
                this.zone.run(this.onXmlImport(XmlRequestType.DiaImagingRVStrain), this, [msg]),
            )
            // Left ventricle Wall Motion
            .on('lvWallMotionStart', (msg) => this.zone.run(this.onUnimplemented('lvWallMotionStart'), this, [msg]))
            .on('lvWallMotionEnd', (msg) => this.zone.run(this.onUnimplemented('lvWallMotionEnd'), this, [msg]))
            .on('lvWallMotionResultClear', (msg) =>
                this.zone.run(this.onUnimplemented('lvWallMotionResultClear'), this, [msg]),
            )
            .on('lvWallMotionResult', (msg) =>
                this.zone.run(this.onXmlImport(XmlRequestType.DiaImagingLVWallMotion), this, [msg]),
            )
            // Misure generiche
            .on('addGroupMeasure', (msg) => this.zone.run(this.onUnimplemented('addGroupMeasure'), this, [msg]))
            .on('deleteMeasure', (msg) => this.zone.run(this.onUnimplemented('deleteMeasure'), this, [msg]))
            .on('modifyMeasure', (msg) => this.zone.run(this.onUnimplemented('modifyMeasure'), this, [msg]));
    }

    //#region Viewer callbacks

    private listenForActions(hub: SignalR.Hub.Proxy) {
        this.actions$.pipe(ofType(seViewerUSMeasureDeleteResponse)).subscribe((action) => {
            hub.invoke(action.callback, action.requestId, action.result);
        });
        this.actions$.pipe(ofType(seViewerUSMeasureGetReportResponse)).subscribe((action) => {
            hub.invoke(action.callback, action.requestId, action.report);
        });
    }

    private onAddKeyImage(msg: DeepPascalCase<KeyImageMessage>) {
        this.store.dispatch(
            seViewerKeyImageAdd({
                image: keyImageFromMessage(msg),
            }),
        );
    }

    private onDeleteKeyImage(msg: DeepPascalCase<KeyImageMessage>) {
        this.store.dispatch(
            seViewerKeyImagesDelete({
                images: [keyImageFromMessage(msg)],
            }),
        );
    }

    private onRefreshKeyImage(msg: DeepPascalCase<KeyImageMessage>) {
        this.store.dispatch(
            seViewerKeyImageRefresh({
                image: keyImageFromMessage(msg),
            }),
        );
    }

    private onSetKeyImages(msg: DeepPascalCase<KeyImageMessage>[]) {
        this.store.dispatch(seViewerKeyImagesSet({ images: msg.map(keyImageFromMessage) }));
    }

    private onSrDelete(msg: any) {
        this.store.dispatch(seViewerSRDelete(convertPropertiesToCamelCase<BaseSRMessage>(msg)));
    }

    private onSrAdd(msg: any) {
        this.store.dispatch(seViewerSRAdd(convertPropertiesToCamelCase<BaseSRMessage>(msg)));
    }

    private onUsMeasureDelete(measure: any, callback: string, requestId: string) {
        this.store.dispatch(
            seViewerUSMeasureDeleteRequest({
                measure: convertPropertiesToCamelCase<USMeasureMessage>(measure),
                callback,
                requestId,
            }),
        );
    }

    private onUsMeasureGetReportInfo(npla: any, callback: string, requestId: string) {
        this.store.dispatch(
            seViewerUSMeasureGetReportRequest({
                nPla: convertPropertiesToCamelCase<PacsTuple>(npla),
                callback,
                requestId,
            }),
        );
    }

    private onUsMeasureImport(msg: any) {
        this.store.dispatch(seViewerUSMeasureImportRequest(convertPropertiesToCamelCase<USMeasureMessage>(msg)));
    }

    private onUsStressEchoInit(msg: any) {
        this.store.dispatch(seViewerUSStressEchoInit(convertPropertiesToCamelCase<USStressEchoInitMessage>(msg)));
    }

    private onUsStressEchoSelect(msg: any) {
        this.store.dispatch(seViewerUSStressEchoSelect(convertPropertiesToCamelCase<USStressEchoSelectMessage>(msg)));
    }

    private onXmlImport(requestType: XmlRequestType) {
        return (msg: any) => {
            switch (requestType) {
                case XmlRequestType.DiaImagingLVStrain:
                case XmlRequestType.DiaImagingRVStrain:
                case XmlRequestType.DiaImagingLVWallMotion:
                    return this.store.dispatch(seViewerUSKineticMeasureImportRequest({ requestType, xml: msg }));
                default:
                    this.store.dispatch(seViewerUSMeasureImportRequestThirdParts({ requestType, xml: msg }));
            }
        };
    }

    private onUnimplemented(messageName: string) {
        return () => {
            this.logger.debug(`Unimplemented viewer message: ${messageName}`);
        };
    }

    //#endregion
}
